import {ICellRendererParams, SetFilterValuesFuncParams} from "@ag-grid-community/core";
import {Bucket, Exact, GetDistinctValuesQuery, InputMaybe} from "../../generated/graphql";
import log from "loglevel";
import {ApolloQueryResult, LazyQueryResult, QueryLazyOptions} from "@apollo/client";
import {Column, GetRowIdFunc, ProcessCellForExportParams, RowClassParams} from "@ag-grid-community/core";
import {Skill} from "../../generated/graphql";
import {AgJob} from "../../components/job/JobPanel";
import {activePendingActions, PendingAssignment} from "../assignment/PendingAssignment";

import type {GridOptions as TGridOptions} from "@ag-grid-community/core/dist/cjs/es5/entities/gridOptions";
import {format} from "date-fns";

export const AG_GRID_NOT_EXIST_FILTER = null;

export const getColumnIdToFields = (columns: Column[], excludeFields: string[] = []) => {
  return columns
    .filter((x) => !excludeFields.includes(x.getId()))
    .reduce((map, col) => {
      const colId = col.getColId();
      map[colId] = col.getColDef().field?.split(",") || [colId];
      return map;
    }, {} as {[key: string]: string[]});
};

export const getOrderSkillFilterParams = (skillsData: Skill[]) => {
  return {
    values: [AG_GRID_NOT_EXIST_FILTER, ...skillsData.map((skill) => skill.id)],
    valueFormatter: (params: any) => {
      if (params.value === AG_GRID_NOT_EXIST_FILTER) {
        return "No Skills";
      }
      const skill = skillsData.find((s) => s.id === params.value);
      return skill ? skill.name : params.value;
    },
    comparator: (a: number, b: number) => {
      const skillA = skillsData.find((s) => s.id === a);
      const skillB = skillsData.find((s) => s.id === b);
      const nameA = skillA ? skillA.name : "";
      const nameB = skillB ? skillB.name : "";
      return nameA.localeCompare(nameB);
    }
  };
};

export const orderSkillsValueGetter = (params: any, skillsData: Skill[]) => {
  if (!params.data?.order?.orderSkills) {
    return "";
  }
  const skillNames = params.data.order.orderSkills
    .map((skill: any) => {
      const foundSkill = skillsData.find((s) => s.id === skill.id);
      return foundSkill ? foundSkill.name : null;
    })
    .filter((name: string | null) => name !== null);
  return skillNames.join(", ");
};

export const getJobRowId: GetRowIdFunc<AgJob> = (params) => params.data.jobId.toString();

export const getJobRowClass = (params: RowClassParams<AgJob>, pendingActions: PendingAssignment[]) => {
  if (
    params.node.data?.hide ||
    activePendingActions(pendingActions, true)
      .flatMap((x) => x.jobIds)
      .includes(params.data?.jobId as number)
  ) {
    try {
      params.node.setRowHeight(0);
      setTimeout(() => {
        params.api.onRowHeightChanged();
      }, 0);
    } catch (error) {
      log.error("SKIPPED: Error redrawing rows: ", error);
    }
    return "hide-row";
  }
  return "";
};

export const clearAllSort = (params: ICellRendererParams) => {
  const allColumns = params.columnApi.getColumns() ?? [];
  const columnState = allColumns.map((column) => {
    return {
      colId: column.getColId(),
      sort: null
    };
  });
  params.columnApi.applyColumnState({state: columnState});
};

export const getFilterValues = (
  params: SetFilterValuesFuncParams,
  getDistinctValues: (
    options?:
      | QueryLazyOptions<Exact<{field: string; typeName: string; limit?: InputMaybe<number> | undefined}>>
      | undefined
  ) => Promise<LazyQueryResult<GetDistinctValuesQuery, Exact<any>>>,
  typename: string,
  field: string
) => {
  getDistinctValues({variables: {typeName: typename, field: field}})
    .then((x: ApolloQueryResult<any>) => {
      params.success(x.data.getDistinctValues.items.map((y: Bucket) => y.key));
    })
    .catch((error: Error) => {
      log.error(error);
    });
};

export const JobGridOptions: TGridOptions<AgJob> = {
  getRowId: getJobRowId,
  defaultCsvExportParams: {
    processCellCallback(params: ProcessCellForExportParams): string {
      const colId = params.column.getColId();
      if (["jobNumber"].includes(colId)) {
        return params.value.text;
      }
      if (["readyAt", "dueBy", "order.createdDateTime"].includes(colId)) {
        return [params.value.lowerField.value, params.value.upperField.value].filter(Boolean).join(" ");
      }
      if (["time", "stops.dispatchZone", "stops.address"].includes(colId)) {
        return [params.value.upperField.value, params.value.lowerField.value].filter(Boolean).join(" - ");
      }
      return params.value;
    },
    fileName: `${format(new Date(), "MMM d")} - Unassigned Jobs`
  }
};
